import { Link } from "react-router-dom";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import CanvasParticles from "../components/CanvasParticles";

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

export default function ContactSupportPage() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ mode: "onTouched", defaultValues: { phone: "+" } });

  const { ref: hookFormPhoneRef, ...phoneRegister } = register("phone", {
    required: "Phone number is required",
    validate: (value) => {
      try {
        return parsePhoneNumberWithError(value).isValid() || "Enter a valid phone number";
      } catch {
        return "Enter a valid phone number";
      }
    },
  });

  const phoneRef = useRef<HTMLInputElement | null>(null);

  const setPhoneRef = (el: HTMLInputElement | null) => {
    phoneRef.current = el;
    hookFormPhoneRef(el);
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const serviceKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End", "Tab"];
    if (serviceKeys.includes(e.key)) return;
    if (/^\d$/.test(e.key)) return;
    if (e.key === "+" && e.currentTarget.selectionStart === 0 && !e.currentTarget.value.startsWith("+")) return;
    e.preventDefault();
  };

  const onSubmit = async (data: FormData) => {
    setServerError(null);

    let text = "";
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: data.subject,
      to_email: "support@novavertextech.com",
      subject: `Support Request: ${data.subject}`,
    };

    try {
      const res = await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Server error");

      setSubmitted(true);
      reset();
    } catch {
      setServerError("Failed to send message. Please try again or email us directly.");
    }
  };

  const inputBase =
    "block w-full bg-transparent border-b text-white text-sm py-2.5 focus:outline-none placeholder-white/20 transition-colors";
  const inputOk = "border-white/10 focus:border-white/30";
  const inputErr = "border-red-500/60 focus:border-red-500";

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? <p className="mt-1 text-xs text-red-400">{msg}</p> : null;

  return (
    <div className="min-h-screen font-sans relative overflow-x-hidden bg-[#0a0a0a]">
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
      </div>

      <header className="bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl" />
              </div>
            </Link>
            <h1 className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2">
              Contact Support
            </h1>
            <div className="w-8" />
          </div>
        </div>
      </header>

      <section className="relative min-h-[30vh] flex items-center overflow-hidden">
        <CanvasParticles />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#0a0a0a]" />
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-12 md:py-16">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-white text-2xl md:text-4xl lg:text-5xl font-light tracking-tight">
              We're Here to Help
            </h2>
            <p className="text-white/50 text-base md:text-lg mt-3 max-w-xl mx-auto">
              Need assistance with your subscription, billing, or account? Our team is ready.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#111111]/60 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-6 md:p-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white/[0.05] rounded-xl shrink-0">
                    <i className="ri-mail-line text-white/60 text-lg" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-xs uppercase tracking-wider mb-1">Email</p>
                    <a
                      href="mailto:support@novavertextech.com"
                      className="text-white/60 hover:text-white transition-colors text-sm"
                    >
                      support@novavertextech.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white/[0.05] rounded-xl shrink-0">
                    <i className="ri-phone-line text-white/60 text-lg" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-xs uppercase tracking-wider mb-1">Phone</p>
                    <a
                      href="tel:+447520663474"
                      className="text-white/60 hover:text-white transition-colors text-sm"
                    >
                      +44 7520 663474
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white/[0.05] rounded-xl shrink-0">
                    <i className="ri-map-pin-line text-white/60 text-lg" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-xs uppercase tracking-wider mb-1">Address</p>
                    <p className="text-white/60 text-sm leading-relaxed">
                      Nova Vertex LTD<br />
                      66 Lavender Hill<br />
                      London, SW11 5RQ<br />
                      United Kingdom
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-[#111111]/60 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-6 md:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/[0.05] rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-white text-2xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Message Sent</h3>
                  <p className="text-white/50 text-sm max-w-sm mx-auto">
                    Thank you for reaching out. Our support team will review your message and get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-block mt-6 bg-white text-[#0a0a0a] px-6 py-3 rounded-full font-medium hover:bg-white/90 transition-colors whitespace-nowrap text-sm"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="firstName" className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                        First Name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        placeholder="John"
                        className={`${inputBase} ${errors.firstName ? inputErr : inputOk}`}
                        {...register("firstName", {
                          required: "First name is required",
                          minLength: { value: 2, message: "At least 2 characters" },
                        })}
                      />
                      <ErrorMsg msg={errors.firstName?.message} />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        placeholder="Doe"
                        className={`${inputBase} ${errors.lastName ? inputErr : inputOk}`}
                        {...register("lastName", {
                          required: "Last name is required",
                          minLength: { value: 2, message: "At least 2 characters" },
                        })}
                      />
                      <ErrorMsg msg={errors.lastName?.message} />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="email" className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="john@company.com"
                      className={`${inputBase} ${errors.email ? inputErr : inputOk}`}
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: "Enter a valid email address",
                        },
                      })}
                    />
                    <ErrorMsg msg={errors.email?.message} />
                  </div>

                  <div className="mt-5">
                    <label htmlFor="phone" className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      placeholder="+"
                      className={`${inputBase} ${errors.phone ? inputErr : inputOk}`}
                      ref={setPhoneRef}
                      onKeyDown={handlePhoneKeyDown}
                      {...phoneRegister}
                    />
                    <ErrorMsg msg={errors.phone?.message} />
                  </div>

                  <div className="mt-5">
                    <label htmlFor="subject" className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      className={`${inputBase} appearance-none cursor-pointer ${errors.subject ? inputErr : inputOk}`}
                      {...register("subject", { required: "Please select a topic" })}
                    >
                      <option value="" className="bg-[#141414]">Select a topic</option>
                      <option value="billing" className="bg-[#141414]">Billing &amp; Payments</option>
                      <option value="subscription" className="bg-[#141414]">Subscription</option>
                      <option value="account" className="bg-[#141414]">Account Access</option>
                      <option value="technical" className="bg-[#141414]">Technical Issue</option>
                      <option value="refund" className="bg-[#141414]">Refund Request</option>
                      <option value="other" className="bg-[#141414]">Other</option>
                    </select>
                    <ErrorMsg msg={errors.subject?.message} />
                  </div>

                  <div className="mt-5">
                    <label htmlFor="message" className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      placeholder="Describe your issue in detail..."
                      className={`${inputBase} resize-none ${errors.message ? inputErr : inputOk}`}
                      {...register("message", {
                        required: "Message is required",
                        minLength: { value: 10, message: "At least 10 characters" },
                        maxLength: { value: 500, message: "Maximum 500 characters" },
                      })}
                    />
                    <ErrorMsg msg={errors.message?.message} />
                    {!errors.message && (
                      <p className="text-white/20 text-xs mt-1">Maximum 500 characters</p>
                    )}
                  </div>

                  {serverError && (
                    <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white text-[#0a0a0a] text-sm font-medium hover:bg-white/90 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending…" : "Send Message"}
                    <span className="w-8 h-8 rounded-full bg-[#0a0a0a] flex items-center justify-center">
                      {isSubmitting ? (
                        <i className="ri-loader-4-line text-white text-sm animate-spin" />
                      ) : (
                        <i className="ri-arrow-right-line text-white text-sm" />
                      )}
                    </span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
